/**
 * Carry-over in millimetres
 *
 * Where a design goes when it is carried from the master position to
 * another product's area (R19, R36). The rule itself is the existing one in
 * `design/uniformRescale.ts` (carry-over 3.0: product class, percent box,
 * RUNDUM, otherwise relative width and centre kept); this module only feeds it millimetres:
 * the relative view is computed from the master placement and the master
 * area, and the result is a placement on the target area. No template is
 * involved (R44).
 */

import {
  carryOverPlacement,
  rescaleCanvasObjectsJSON,
} from '../design/uniformRescale.js';
import type { CarryOverRuleOptions } from '../design/carryOverRule.js';
import type { CanvasObjectJSON } from '../design/computeDesignState.js';
import type { CanvasJsonLike } from './canvasJsonMm.js';
import {
  placementAreaFromMm,
  placementMmToRelative,
  relativeToPlacementMm,
  type PlacementAreaMm,
  type PlacementMm,
} from './placementMm.js';

/**
 * The placement a derived position gets from the master placement (AE5,
 * AE20). The rotation is metadata and travels unchanged. The carry-over
 * rule (product class, percent box, RUNDUM) decides from the areas' fields;
 * `options` says whether the target is the master product itself.
 */
export function carryOverPlacementMm(
  master: PlacementMm,
  masterArea: PlacementAreaMm,
  targetArea: PlacementAreaMm,
  options: CarryOverRuleOptions = { isMaster: false }
): PlacementMm {
  const rel = carryOverPlacement(
    placementMmToRelative(master, masterArea),
    placementAreaFromMm(masterArea),
    placementAreaFromMm(targetArea),
    options
  );
  return relativeToPlacementMm(rel, targetArea, master.rotation);
}

/**
 * The master's canvas objects (millimetre space, area-relative) moved onto
 * the target area at the given placement, with one uniform factor. The
 * bounding box of the result is the placement (AE20).
 */
export function rescaleCanvasObjectsMm(
  objects: CanvasObjectJSON[],
  masterArea: PlacementAreaMm,
  targetArea: PlacementAreaMm,
  targetPlacement: PlacementMm
): CanvasObjectJSON[] {
  return rescaleCanvasObjectsJSON(
    objects,
    placementAreaFromMm(masterArea),
    placementAreaFromMm(targetArea),
    placementMmToRelative(targetPlacement, targetArea)
  );
}

/** What a position row stores: its placement and its canvas JSON (mm space). */
export interface PositionMm {
  placementMm: PlacementMm;
  /** Canvas JSON in millimetre space; null for a crop-only row. */
  canvasData: CanvasJsonLike | null;
}

/**
 * The derived row of a target product, from the master row (S2, AE20):
 * the placement by the carry-over rule, the master's objects moved onto it
 * with one uniform factor. Keys of the canvas JSON other than the objects
 * (version, background) travel unchanged; a crop-only master gives a
 * crop-only derived row. Every writer of the master position derives its
 * rows through here (Creator save, batch-apply, bulk, agent tools).
 */
export function carryOverPositionMm(
  master: PositionMm,
  masterArea: PlacementAreaMm,
  targetArea: PlacementAreaMm,
  options: CarryOverRuleOptions = { isMaster: false }
): PositionMm {
  const placementMm = carryOverPlacementMm(
    master.placementMm,
    masterArea,
    targetArea,
    options
  );
  const objects = master.canvasData?.objects;
  if (!master.canvasData || !Array.isArray(objects) || objects.length === 0) {
    return { placementMm, canvasData: master.canvasData };
  }
  return {
    placementMm,
    canvasData: {
      ...master.canvasData,
      objects: rescaleCanvasObjectsMm(
        objects,
        masterArea,
        targetArea,
        placementMm
      ),
    },
  };
}
